import {
  catchError,
  map,
  type MonoTypeOperatorFunction,
  type OperatorFunction,
  throwError,
} from 'rxjs';

import { environment } from '../../../environments/environment';
import { readProp } from '../../shared/utils/parse';
import { ApiError, type ApiErrorCode, errorStatus, isApiError } from '../api/api-error';

/** Конверт бэкенда: данные всегда в `response`, при ошибке заполнены `code` и `description`. */
export interface ApiEnvelope<T> {
  message: string | null;
  code: string | null;
  description: string | null;
  response: T | null;
}

/** Хвостовой слэш срезаем один раз: иначе каждый путь давал бы `//` и бэкенд отвечал бы 404. */
const API_BASE = environment.apiUrl.replace(/\/+$/, '');

/** Whitelist: незнакомый серверный код разбирается по статусу, а не ломает экран чужим текстом. */
const SERVER_CODES = new Map<string, ApiErrorCode>([
  ['ORGANIZATION_NOT_FOUND', 'org_not_found'],
  ['CODE_INCORRECT', 'invalid_code'],
  ['TIME_EXPIRED', 'code_expired'],
  ['TOO_MANY_LOGIN_REQUEST', 'too_many_attempts'],
  ['TOO_MANY_REQUEST', 'too_many_requests'],
  ['ADDRESS_LIMIT_EXCEEDED', 'address_limit'],
  ['BRANCH_NOT_FOUND', 'branch_not_found'],
  ['CASHBOX_NOT_FOUND', 'cashbox_not_found'],
  ['POSITION_NOT_FOUND', 'position_not_found'],
  ['ORDER_ITEMS_UNAVAILABLE', 'items_unavailable'],
  ['DELIVERY_MIN_ORDER_AMOUNT', 'min_order_amount'],
  ['DELIVERY_SETTINGS_NOT_CONFIGURED', 'delivery_not_configured'],
  ['ADDRESS_NOT_FOUND', 'not_found'],
  ['ORDER_NOT_FOUND', 'not_found'],
  ['URL_NOT_FOUND', 'not_found'],
  // Кривой телефон бэкенд отбивает именно этим кодом — проверено запросом, в гайде его нет.
  ['JSON_PARSING_ERROR_CODE', 'invalid_phone'],
  ['REQUIRED_FIELD_MISSED', 'unknown'],
  ['INVALID_VALUE', 'unknown'],
]);

export function apiPath(path: string): string {
  return `${API_BASE}/${path.replace(/^\/+/, '')}`;
}

/** Сверяем префикс до слэша, а не подстроку: Bearer не должен уехать на похожий чужой хост. */
export function isApiRequest(url: string): boolean {
  return url === API_BASE || url.startsWith(`${API_BASE}/`);
}

export function unwrapEnvelope<T>(): OperatorFunction<ApiEnvelope<T> | null, T> {
  // Пустой `response` штатен: проверка домена и logout отвечают конвертом без тела, это `void`.
  return map((envelope) => (envelope?.response ?? undefined) as T);
}

export function toApiError(error: unknown): ApiError {
  if (isApiError(error)) {
    return error;
  }
  const status = responseStatus(error);
  return new ApiError(status, serverCode(error) ?? statusCode(status));
}

export function mapApiError<T>(): MonoTypeOperatorFunction<T> {
  return catchError((error: unknown) => throwError(() => toApiError(error)));
}

/** `status: 0` у HttpErrorResponse значит «ответа не было» (обрыв, CORS), а не HTTP-код. */
function responseStatus(error: unknown): number | null {
  const status = errorStatus(error);
  return status === null || status === 0 ? null : status;
}

/** Конверт лежит в `error.error`, но бросить могут и само тело — смотрим оба уровня. */
function serverCode(error: unknown): ApiErrorCode | null {
  return codeOf(readProp(error, 'error')) ?? codeOf(error);
}

function codeOf(body: unknown): ApiErrorCode | null {
  const code = readProp(body, 'code');
  return typeof code === 'string' ? (SERVER_CODES.get(code) ?? null) : null;
}

/** Сырой Spring, HTML от nginx и пустое тело кода не несут — остаётся только HTTP-статус. */
function statusCode(status: number | null): ApiErrorCode {
  switch (status) {
    case null:
      return 'network';
    case 401:
      return 'unauthorized';
    case 404:
      return 'not_found';
    default:
      return 'unknown';
  }
}
